// charts/CartesianChart.jsx
import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * CartesianChart
 * --------------
 * Minimal bar/column chart that flips to horizontal with `horizontal`.
 * Expects:
 *   data = {
 *     categories: string[],                          // x-axis labels in order
 *     series: Array<{ label: string, values: number[], type: 'column' | 'line', color?: string, dashStyle?: string, yAxis?: 0 | 1 }>
 *   }
 * Cosmetic props:
 *   - legend: 'right' | 'bottom' | 'top' | null/undefined to hide
 *   - stacked: boolean
 *   - horizontal: boolean   (false = vertical columns, true = horizontal bars)
 *   - dataLabels: boolean
 *   - xLabel?: string       (axis title for X axis; null/undefined to hide)
 *   - yLabel?: string       (axis title for Y axis; null/undefined to hide),
 *   - y2Label?: string      (axis title for the secondary Y axis; passing it enables the axis for series with yAxis: 1)
 *   - rotateXLabels: boolean (tilt x labels when they don't fit instead of word-wrapping them)
 *   - onClick:         (dataPoint) => void
 *
 * Notes:
 * - Uses object points with stable IDs so value/category updates animate smoothly.
 * - Axis/baseline/gridlines use MUI theme.palette.divider for consistent styling.
 * - A series with yAxis: 1 is plotted against the secondary axis when the caller passes y2Label; otherwise all series share the primary axis.
 */
export const CartesianChart = ({
  data,
  legend,
  stacked = false,
  horizontal = false,
  dataLabels = false,
  xLabel,
  yLabel,
  y2Label,
  xTickFormatter,
  markers = false,
  allowNegative = false,
  rotateXLabels = false,
  isZones = false,
  tooltipFormatter,
  onHover,
  onClick,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  const labelStyle = { color: theme.palette.text.secondary };

  const categories = data?.categories || [];
  if (!data || !categories.length || !data.series) return null;

  const allLines = data.series.every((s) => s.type === 'line');
  // Opt-in: the secondary axis exists only when the caller titles it, regardless of yAxis hints in the data
  const hasSecondaryAxis = y2Label != null;

  // Stable, object-point series so updates tween instead of snapping.
  const highchartsSeries = data.series.map((seriesItem, seriesIndex) => {
    const isLine = seriesItem.type === 'line' || seriesItem.type === 'spline';
    const isColumn = seriesItem.type === 'column' || (!seriesItem.type && !isLine);

    const points = categories.map((categoryLabel, categoryIndex) => {
      const rawY = seriesItem.values[categoryIndex];

      const y = rawY === undefined ? 0 : rawY;

      return {
        x: categoryIndex,
        y,
        id: String(categoryLabel),
      };
    });

    const useAreaStack = stacked && allLines; // true only if *all* are lines and stacking requested

    return {
      id: `${seriesItem.label}__${seriesIndex}`,
      type:
        useAreaStack ? 'areaspline'
        : isLine ? 'spline'
        : seriesItem.type || 'column',
      name: seriesItem.label,
      data: points,
      yAxis: hasSecondaryAxis ? (seriesItem.yAxis ?? 0) : 0,
      color: seriesItem.color,
      dashStyle: seriesItem.dashStyle,
      borderWidth: 0,
      marker:
        isLine ?
          {
            enabled: markers,
            radius: 4,
            symbol: 'circle',
            fillColor: seriesItem.color,
            lineColor: seriesItem.color || theme.palette.primary.main,
            lineWidth: 2,
          }
        : undefined,
      fillOpacity: useAreaStack ? 0.6 : 1,
      stacking:
        (
          useAreaStack // ✅ stack only when stacking all-line chart
        ) ?
          'normal'
        : (
          isColumn && stacked // ✅ stack columns only when stacked prop true
        ) ?
          'normal'
        : undefined,
      zones:
        isLine && isZones ?
          [
            { value: 0, color: theme.palette.error.main }, // below zero
            { color: theme.palette.success.main }, // above zero
          ]
        : undefined,
    };
  });

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : legend === 'top' ? { enabled: true, align: 'center', verticalAlign: 'top' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  // Extra spacing for top/bottom legend; otherwise compact.
  const chartSpacing =
    legend === 'bottom' ? [8, 8, 24, 8]
    : legend === 'top' ? [24, 8, 8, 8]
    : [8, 8, 8, 8];

  const options = {
    chart: {
      inverted: !!horizontal, // horizontal bars when true
      spacing: chartSpacing,
      animation: { duration: 300 },
      // height/background/font handled in BaseHighchart / global setOptions
    },
    title: { text: undefined },
    legend: legendOptions,

    // Axes styled with MUI divider.
    // Baseline on xAxis; gridlines on yAxis.
    // When inverted (horizontal), this yields a solid left baseline and vertical gridlines.
    xAxis: {
      categories, // labels only; points use x=index
      labels: {
        formatter: function () {
          return xTickFormatter ? xTickFormatter(this.value, this.pos) : this.value;
        },
        ...(rotateXLabels && { autoRotation: [-45], autoRotationLimit: Infinity }),
      },
      tickLength: 0,
      lineWidth: 1, // solid baseline
      lineColor: dividerColor,
      gridLineWidth: 0, // no gridlines here; yAxis provides them
      title: {
        text: xLabel || null, // hide when null/undefined/empty
        style: labelStyle,
        margin: 10,
      },
    },
    yAxis: [
      {
        min: !allowNegative ? 0 : null, // force baseline at 0
        title: {
          text: yLabel || null, // hide when null/undefined/empty
          style: labelStyle,
        },
        lineWidth: 0,
        gridLineWidth: 1, // faint gridlines
        gridLineColor: dividerColor,
        gridLineDashStyle: 'ShortDot',
      },
      // Secondary axis shares the primary's gridlines so the plot area isn't double-ruled
      ...(hasSecondaryAxis ?
        [
          {
            opposite: true,
            min: !allowNegative ? 0 : null,
            title: {
              text: y2Label || null,
              style: labelStyle,
            },
            lineWidth: 0,
            gridLineWidth: 0,
          },
        ]
      : []),
    ],

    tooltip: {
      shared: true,
      formatter:
        tooltipFormatter ?
          function () {
            const points = this.points || (this.point ? [this.point] : []);
            const category =
              points[0]?.point?.category ??
              points[0]?.category ??
              this.key ??
              (typeof this.x === 'number' ? categories[this.x] : this.x);

            return tooltipFormatter({
              category,
              points: points.map((point) => ({
                seriesName: point.series?.name,
                y: point.y,
                color: point.color,
                point,
              })),
              raw: this,
            });
          }
        : undefined,
    },

    plotOptions: {
      series: {
        stacking: stacked ? 'normal' : undefined,
        animation: { duration: 300 },
        dataLabels: { enabled: dataLabels, padding: 2 },
        groupPadding: 0.12,
        pointPadding: 0.04,
        turboThreshold: 0,
        cursor: onClick ? 'pointer' : 'default',

        point: {
          events: {
            mouseOver(e) {
              const category = e.target.category;
              if (onHover) onHover(category);
            },
            mouseOut() {
              if (onHover) onHover(null);
            },
            click() {
              if (onClick) onClick(this.options.original ?? this);
            },
          },
        },
      },
      areaspline: {
        stacking: stacked && allLines ? 'normal' : undefined,
        pointPlacement: 'on', // ✅ ensures first/last points align with edges
      },
      column: { pointPlacement: null },
      line: { pointPlacement: 'on' },
      spline: { pointPlacement: 'on' },
    },

    series: highchartsSeries,
  };

  return <BaseHighchart options={options} />;
};
